import type { DraftItem, EntryInput, EntrySource } from '../../../shared/api';
import type { ISODate, Meal } from '../../../shared/dates';
import { MEAL_LABELS } from '../../../shared/dates';
import { scaleMap } from '../../../shared/nutrients';
import { useRef } from 'react';
import { useAddEntries, useCreateCustomFood, useDeleteEntry } from '../../api/hooks';
import { useUI } from '../../lib/ui';
import type { LogItem } from './logItem';

export const CONFIDENCE_SCORE = { high: 0.9, medium: 0.7, low: 0.4 } as const;

export type { LogItem };

export function draftToLogItem(d: DraftItem, source: EntrySource): LogItem {
  return {
    name: d.name,
    brand: d.brand,
    foodRef: d.foodRef,
    grams: d.grams,
    per100: d.per100,
    servings: d.servings,
    source,
    confidence: source === 'ai_text' || source === 'ai_photo' || source === 'what_fits' ? CONFIDENCE_SCORE[d.confidence] : null,
  };
}

/** Log items with an undo toast. Label scans are saved as the person's own food first. */
export function useLogItems(date: ISODate, meal: Meal) {
  const { toast } = useUI();
  const add = useAddEntries();
  const remove = useDeleteEntry();
  const createFood = useCreateCustomFood();
  // Batches being logged right now. A double tap on the same control is
  // ignored; a tap on a different food still goes through.
  const inFlight = useRef(new Set<string>());

  const log = async (items: LogItem[], opts: { quiet?: boolean } = {}) => {
    if (items.length === 0) return false;
    const signature = items.map((i) => `${i.foodRef ?? i.name}:${i.grams}`).join('|');
    if (inFlight.current.has(signature)) return false;
    inFlight.current.add(signature);
    try {
      const prepared: EntryInput[] = [];
      for (const item of items) {
        let foodRef = item.foodRef;
        if (!foodRef && item.source === 'ai_label') {
          const serving = item.servings[0] ?? { label: `${item.grams} g`, grams: item.grams };
          const food = await createFood.mutateAsync({
            name: item.name,
            brand: item.brand,
            servingLabel: serving.label,
            servingGrams: serving.grams,
            perServing: scaleMap(item.per100, serving.grams),
          });
          foodRef = food.ref;
        }
        prepared.push({
          date,
          meal,
          foodRef,
          name: item.name,
          brand: item.brand,
          grams: item.grams,
          per100: item.per100,
          servings: item.servings,
          source: item.source,
          confidence: item.confidence ?? null,
        });
      }
      const created = await add.mutateAsync(prepared);
      const totalKcal = created.reduce((s, e) => s + (scaleMap(e.per100, e.grams).kcal ?? 0), 0);
      if (!opts.quiet || created.length > 0) {
        toast(
          created.length === 1
            ? `Logged ${created[0]!.name} · ${Math.round(totalKcal)} kcal`
            : `Logged ${created.length} items to ${MEAL_LABELS[meal]} · ${Math.round(totalKcal)} kcal`,
          {
            action: {
              label: 'Undo',
              onClick: () => created.forEach((e) => remove.mutate({ id: e.id, date: e.date })),
            },
          },
        );
      }
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not log that', { tone: 'error' });
      return false;
    } finally {
      inFlight.current.delete(signature);
    }
  };

  return { log, pending: add.isPending || createFood.isPending };
}
